// @flow
import { all, call, fork, takeEvery, put } from "redux-saga/effects"

import {
  CHANGE_COLOR,
  CHANGE_LAYOUT,
  CHANGE_LAYOUT_WIDTH,
  CHANGE_SIDEBAR_THEME,
  CHANGE_SIDEBAR_TYPE,
  CHANGE_TOPBAR_THEME,
  SHOW_RIGHT_SIDEBAR,
  CHANGE_MODE
} from "./actionTypes"

import {
  changeSidebarType as changeSidebarTypeAction,
  changeTopbarTheme as changeTopbarThemeAction
} from "./actions"

/**
 * Changes the body attribute
 */
function changeBodyAttribute(attribute: string, value: string): boolean {
  if (document.body) document.body.setAttribute(attribute, value)
  return true
}

const changeHTMLAttribute = (attribute: string, value: string): boolean => {
  if (document.documentElement) document.documentElement.setAttribute(attribute, value);
  return true;
}

/**
 * Toggle the class on body
 * @param {*} cssClass
 */
function manageBodyClass(cssClass: string, action: string = "toggle"): boolean {
  switch (action) {
    case "add":
      if (document.body) document.body.classList.add(cssClass)
      break
    case "remove":
      if (document.body) document.body.classList.remove(cssClass)
      break
    default:
      if (document.body) document.body.classList.toggle(cssClass)
      break
  }

  return true
}

/**
 * Changes the layout color
 * @param {*} param0
 */
interface Action<T = any> {
  type: string
  payload?: T
}

function* changeColor(action: Action<string>): Generator<any, void, any> {
  const { payload: color } = action
  try {
    yield call(changeHTMLAttribute as any, "data-theme-mode", color)
  } catch (error: any) { }
}

function* changeModeTheme(action: Action<string>): Generator<any, void, any> {
  const { payload: mode } = action
  try {
    yield call(changeHTMLAttribute as any, "data-bs-theme", mode)
  } catch (error: any) { }
}


/**
 * Changes the layout type
 * @param {*} param0
 */
function* changeLayout(action: Action<string>): Generator<any, void, any> {
  const { payload: layout } = action
  try {
    yield put(changeTopbarThemeAction("light"))
    yield call(changeBodyAttribute as any, "data-layout", layout || "vertical")
  } catch (error: any) { }
}

/**
 * Changes the layout width
 * @param {*} param0
 */
function* changeLayoutWidth(action: Action<string>): Generator<any, void, any> {
  const { payload: width } = action
  try {
    if (width === "boxed") {
      yield put(changeSidebarTypeAction("icon"))
      yield call(changeBodyAttribute as any, "data-layout-size", width)
    } else {
      yield put(changeSidebarTypeAction("default"))
      yield call(changeBodyAttribute as any, "data-layout-size", width)
    }
  } catch (error: any) { }
}

/**
 * Changes the left sidebar theme
 * @param {*} param0
 */
function* changeLeftSidebarTheme(action: Action<string>): Generator<any, void, any> {
  const { payload: theme } = action
  try {
    yield call(changeBodyAttribute as any, "data-sidebar", theme)
  } catch (error: any) { }
}

/**
 * Changes the topbar theme
 * @param {*} param0
 */
function* changeTopbarTheme(action: Action<string>): Generator<any, void, any> {
  const { payload: theme } = action
  try {
    yield call(changeBodyAttribute as any, "data-topbar", theme)
  } catch (error: any) { }
}

/**
 * Changes the left sidebar type
 * @param {*} param0
 */
function* changeLeftSidebarType(action: Action<{ sidebarType: string; isMobile?: boolean }>): Generator<any, void, any> {
  const { payload } = action
  const { sidebarType, isMobile } = payload || { sidebarType: "", isMobile: false }
  try {
    switch (sidebarType) {
      case "compact":
        yield call(changeBodyAttribute, "data-sidebar-size", "small")
        yield call(manageBodyClass, "sidebar-enable", "remove")
        yield call(manageBodyClass, "vertical-collpsed", "remove")
        break
      case "icon":
        yield call(changeBodyAttribute, "data-keep-enlarged", "true")
        yield call(manageBodyClass, "vertical-collpsed", "add")
        break
      case "condensed":
        // yield call(manageBodyClass, "sidebar-enable", "add")
        // if (!isMobile) yield call(manageBodyClass, "vertical-collpsed", "add")
        yield call(manageBodyClass, "sidebar-enable", "add")
        if (window.screen.width >= 992) {
          yield call(manageBodyClass, "vertical-collpsed", "remove")
          yield call(manageBodyClass, "sidebar-enable", "remove")
          yield call(manageBodyClass, "vertical-collpsed", "add")
          yield call(manageBodyClass, "sidebar-enable", "add")
        } else {
          yield call(manageBodyClass, "sidebar-enable", "add")
          yield call(manageBodyClass, "vertical-collpsed", "add")
        }
        break
      default:
        yield call(changeBodyAttribute, "data-sidebar-size", "")
        yield call(manageBodyClass, "sidebar-enable", "remove")
        if (!isMobile)
          yield call(manageBodyClass, "vertical-collpsed", "remove")
        break
    }
  } catch (error: any) { }
}

/**
 * Show the rightsidebar
 */
function* showRightSidebar(): Generator<any, void, any> {
  try {
    yield call(manageBodyClass, "right-bar-enabled", "add")
  } catch (error) { }
}

/**
 * Watchers
 */
export function* watchChangeLayoutType() {
  yield takeEvery(CHANGE_LAYOUT, changeLayout)
}

export function* watchChangeLayoutWidth() {
  yield takeEvery(CHANGE_LAYOUT_WIDTH, changeLayoutWidth)
}

export function* watchChangeColorType() {
  yield takeEvery(CHANGE_COLOR, changeColor)
}

export function* watchChangeModeType() {
  yield takeEvery(CHANGE_MODE, changeModeTheme)
}

export function* watchChangeLeftSidebarTheme() {
  yield takeEvery(CHANGE_SIDEBAR_THEME, changeLeftSidebarTheme)
}

export function* watchChangeLeftSidebarType() {
  yield takeEvery(CHANGE_SIDEBAR_TYPE, changeLeftSidebarType)
}

export function* watchChangeTopbarTheme() {
  yield takeEvery(CHANGE_TOPBAR_THEME, changeTopbarTheme)
}

export function* watchShowRightSidebar() {
  yield takeEvery(SHOW_RIGHT_SIDEBAR, showRightSidebar)
}

function* LayoutSaga() {
  yield all([
    fork(watchChangeLayoutType),
    fork(watchChangeColorType),
    fork(watchChangeModeType),
    fork(watchChangeLayoutWidth),
    fork(watchChangeLeftSidebarTheme),
    fork(watchChangeLeftSidebarType),
    fork(watchShowRightSidebar),
    fork(watchChangeTopbarTheme),
  ])
}

export default LayoutSaga
